import i18n from 'i18n-literally'
import { createSelector } from 'redux-bundler'

import ms from 'milliseconds'
import reduceReducers from 'reduce-reducers'
import createAsyncResourceBundle from 'redux-bundler/dist/create-async-resource-bundle'

import createLogger from '~/src/Lib/Logging'
import { parseApiErrors } from '~/src/Lib/Utils'

const logger = createLogger('Device/bundle#found')

export const FOUND_DEVICES_DIALOG_URL = '/devices/search'

export const POLLING_DURATION = ms.minutes(15)
export const POLLING_INTERVAL = ms.seconds(10)

export const SERIAL_PREFIX_TO_MODEL = {
  get H110() {
    return i18n`Repeater`
  },
  get H111() {
    return i18n`Climate Station`
  },
  get H130() {
    return i18n`Repeater`
  },
  get H210() {
    return i18n`Gateway`
  },
  get H311() {
    return i18n`M8 Sensor`
  },
  get H321() {
    return i18n`M8 Sensor`
  },
  get H331() {
    return i18n`M8 Sensor`
  },
  get H340() {
    return i18n`Repeater`
  },
  get H341() {
    return i18n`TEROS 12`
  },
  get H342() {
    return this.H341
  },
  get H343() {
    return i18n`TEROS ONE`
  },
  get H344() {
    return this.H343
  },
  get H346() {
    return i18n`TEROS 22`
  },
  get H411() {
    return i18n`Climate Station`
  },
  get H412() {
    return i18n`ATMOS 14`
  },
  get H421() {
    return i18n`CLIMATE ONE`
  },
  get H520() {
    return i18n`pH Sensor`
  },
  get H620() {
    return i18n`Quantum`
  },
  get H710() {
    return i18n`Solenoid Controller`
  },
  get H711() {
    return this.H710
  },
  get H810() {
    return i18n`Drip & Drain Station`
  },
  get H820() {
    return this.H810
  },
  get H821() {
    return this.H810
  },
  get H910() {
    return i18n`Pressure Sensor`
  },
}

// Default action types - `createAsyncResourceBundle`
export const FOUND_DEVICES_CLEARED = 'FOUND_DEVICES_CLEARED'
export const FOUND_DEVICES_FETCH_FAILED = 'FOUND_DEVICES_FETCH_FAILED'
export const FOUND_DEVICES_FETCH_FINISHED = 'FOUND_DEVICES_FETCH_FINISHED'
export const FOUND_DEVICES_FETCH_STARTED = 'FOUND_DEVICES_FETCH_STARTED'

// Polling API state
export const FOUND_DEVICES_POLLING_STARTED = 'FOUND_DEVICES_POLLING_STARTED'
export const FOUND_DEVICES_POLLING_STOPPED = 'FOUND_DEVICES_POLLING_STOPPED'
export const FOUND_DEVICES_SET_POLLING_TIMEOUT = 'FOUND_DEVICES_SET_POLLING_TIMEOUT'
export const FOUND_DEVICES_RESET = 'FOUND_DEVICES_RESET'

// Gateway beaconing API state
export const OPEN_JOIN_MODE_DISABLED = 'OPEN_JOIN_MODE_DISABLED'
export const OPEN_JOIN_MODE_ENABLED = 'OPEN_JOIN_MODE_ENABLED'
export const OPEN_JOIN_MODE_FAILED = 'OPEN_JOIN_MODE_FAILED'
export const OPEN_JOIN_MODE_INFLIGHT = 'OPEN_JOIN_MODE_INFLIGHT'

export const additionalState = {
  inflight: null,
  openJoinError: null,
  pollingLastUpdated: null,
  pollingStart: null,
  pollingTimeoutID: null,
}

const foundDevicesBundle = createAsyncResourceBundle({
  name: 'foundDevices',
  actionBaseType: 'FOUND_DEVICES',
  getPromise: ({ apiFetch }) => apiFetch('/self_provisioning/').then(res => res.devices),
  retryAfter: Infinity,
  staleAfter: Infinity,
})

export default {
  ...foundDevicesBundle,
  init: store => store.dispatch({ type: FOUND_DEVICES_RESET }),
  reducer: reduceReducers(foundDevicesBundle.reducer, (state, action) => {
    switch (action.type) {
      case FOUND_DEVICES_RESET:
        return { ...state, inflight: null, pollingTimeoutID: null }
      case FOUND_DEVICES_FETCH_STARTED:
        return { ...state, pollingLastUpdated: +new Date() }
      case FOUND_DEVICES_FETCH_FINISHED:
        return { ...state, pollingLastUpdated: +new Date() }
      case FOUND_DEVICES_FETCH_FAILED:
        return { ...state, pollingLastUpdated: +new Date() }
      case FOUND_DEVICES_POLLING_STARTED:
        return { ...state, pollingLastUpdated: +new Date(), pollingStart: +new Date(), pollingTimeoutID: null }
      case FOUND_DEVICES_POLLING_STOPPED:
        return { ...state, pollingLastUpdated: +new Date(), pollingStart: null, pollingTimeoutID: null }
      case FOUND_DEVICES_SET_POLLING_TIMEOUT:
        return { ...state, pollingTimeoutID: action.payload }
      case OPEN_JOIN_MODE_INFLIGHT:
        return { ...state, inflight: action.payload, openJoinError: null }
      case OPEN_JOIN_MODE_ENABLED:
        return { ...state, inflight: null }
      case OPEN_JOIN_MODE_DISABLED:
        return { ...state, inflight: null }
      case OPEN_JOIN_MODE_FAILED:
        return { ...state, inflight: null, openJoinError: action.error }
      default:
        if (!Object.keys(additionalState).every(key => key in state)) {
          return { ...additionalState, ...state, data: state.data ?? [] }
        }
        return state
    }
  }),
  doStartFoundDevicesPolling:
    payload =>
    ({ dispatch, store }) => {
      if (payload?.clearDevices === false) {
        // Indicates server should keep previous found devices
      } else {
        // Match server's default behavior and clear cache
        store.doClearFoundDevices()
      }
      dispatch({ type: FOUND_DEVICES_POLLING_STARTED })
      return store.doEnableOpenJoinMode(payload)
    },
  // Not intended to be called directly
  doEnableOpenJoinMode:
    payload =>
    ({ apiFetch, dispatch, store }) => {
      dispatch({ type: OPEN_JOIN_MODE_INFLIGHT, payload: 'enable' })
      return apiFetch('/self_provisioning/enable/', payload, { method: 'POST' })
        .then(response => {
          dispatch({ type: OPEN_JOIN_MODE_ENABLED, payload: response })
          store.doPollDevices()
        })
        .catch(e => {
          const error = parseApiErrors(e) || e.message || 'Unknown error. Please try again.'
          dispatch({ type: OPEN_JOIN_MODE_FAILED, error })
          dispatch({ actionCreator: 'doAddSnackbarMessage', args: [error] })
          store.doStopFoundDevicesPolling()
        })
    },
  // Not intended to be called directly
  doPollDevices:
    () =>
    ({ dispatch, store }) => {
      const { url } = store.selectDialogRouteInfo()
      const { inflight, isLoading, pollingStart, pollingTimeoutID } = store.selectFoundDevicesRaw()

      if (inflight === 'enable') return // Short-circuit if user closed and rapidly re-initiated polling

      const pollingDuration = new Date() - new Date(pollingStart)
      const pollingHasEnded = !pollingStart || pollingDuration >= POLLING_DURATION
      const userOnSearchDialog = url === FOUND_DEVICES_DIALOG_URL

      if (pollingHasEnded) {
        store.doStopFoundDevicesPolling()
      } else {
        if (userOnSearchDialog && !isLoading) {
          // Prevent redundant parallel fetching; e.g. server timeout
          store.doFetchFoundDevices()
        }
        clearTimeout(pollingTimeoutID)
        const timeoutID = setTimeout(() => store.doPollDevices(), POLLING_INTERVAL)
        dispatch({ type: FOUND_DEVICES_SET_POLLING_TIMEOUT, payload: timeoutID })
      }
    },
  doStopFoundDevicesPolling:
    () =>
    ({ dispatch, store }) => {
      const { pollingStart } = store.selectFoundDevicesRaw()
      if (!pollingStart) return null
      dispatch({ type: FOUND_DEVICES_POLLING_STOPPED })
      return store.doDisableOpenJoinMode()
    },
  // Not intended to be called directly
  doDisableOpenJoinMode:
    () =>
    ({ apiFetch, dispatch }) => {
      dispatch({ type: OPEN_JOIN_MODE_INFLIGHT, payload: 'disable' })
      return apiFetch('/self_provisioning/disable/', null, { method: 'POST' })
        .then(response => {
          dispatch({ type: OPEN_JOIN_MODE_DISABLED, payload: response })
        })
        .catch(e => {
          const error = parseApiErrors(e) || e.message
          dispatch({ type: OPEN_JOIN_MODE_FAILED, error })
          // No need to notify the user if this fails
        })
    },
  // Allows a page refresh to detect a false polling state outside of its expected duration
  reactDetectStalePollingState: createSelector('selectFoundDevicesRaw', ({ pollingStart }) => {
    if (!pollingStart) return null
    const pollingDuration = new Date() - new Date(pollingStart)
    if (pollingDuration > POLLING_DURATION * 1.1) {
      logger.debug('[reactor] polling state detected beyond expected duration', {
        pollingDuration: `${pollingDuration / 1000} sec`,
      })
      return { actionCreator: 'doStopFoundDevicesPolling' }
    }
    return null
  }),
  reactResumeDroppedPollingState: createSelector(
    'selectFoundDevicesRaw',
    ({ inflight, pollingStart, pollingTimeoutID }) => {
      // If we haven't started polling (no pollingStart),
      // or polling is actively occuring (pollingTimeoutID),
      // or we're already enabling polling,
      // do nothing
      if (!pollingStart || pollingTimeoutID || inflight === 'enable') return null
      if (new Date() - new Date(pollingStart) <= POLLING_DURATION) {
        logger.debug('[reactor] polling state detected without active timer; resuming')
        return { actionCreator: 'doPollDevices' }
      }
      return null
    },
  ),
  persistActions: [...foundDevicesBundle.persistActions, FOUND_DEVICES_POLLING_STARTED, FOUND_DEVICES_POLLING_STOPPED],
}
